import { useCallback } from 'react'
import { useI18n } from '../../i18n'
import type { CVSkill, SkillLevel } from '../../types/cv'
import './editor.css'

export interface SkillsEditorProps {
  data: CVSkill[]
  onChange: (data: CVSkill[]) => void
}

const SKILL_LEVELS: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert']

/**
 * Skills section editor
 */
export function SkillsEditor({ data, onChange }: SkillsEditorProps) {
  const { t } = useI18n()
  const handleAdd = useCallback(() => {
    const newEntry: CVSkill = {
      id: crypto.randomUUID(),
      name: '',
      level: 'intermediate',
      category: '',
      sortOrder: data.length,
    }
    onChange([...data, newEntry])
  }, [data, onChange])

  const handleRemove = useCallback(
    (id: string) => {
      onChange(data.filter((item) => item.id !== id))
    },
    [data, onChange]
  )

  const handleChange = useCallback(
    (id: string, field: keyof CVSkill, value: string) => {
      onChange(data.map((item) => (item.id === id ? { ...item, [field]: value } : item)))
    },
    [data, onChange]
  )

  // Group skills by category for display
  const categories = [...new Set(data.map((s) => s.category || 'Other'))]

  return (
    <>
      <ul className="editor-entry-list" aria-label={t('editor.lists.skills')}>
        {data.map((skill) => {
          const skillLabel = skill.name || t('editor.entries.skill')
          return (
            <li
              key={skill.id}
              className="editor-entry editor-entry-compact"
              aria-label={skillLabel}
            >
              <div className="editor-grid editor-grid-skills">
                <div className="editor-field">
                  <label htmlFor={`skill-name-${skill.id}`}>{t('fields.skillName')}</label>
                  <input
                    id={`skill-name-${skill.id}`}
                    type="text"
                    value={skill.name}
                    onChange={(e) => handleChange(skill.id, 'name', e.target.value)}
                    placeholder={t('placeholders.skillName')}
                  />
                </div>

                <div className="editor-field">
                  <label htmlFor={`skill-level-${skill.id}`}>{t('fields.level')}</label>
                  <select
                    id={`skill-level-${skill.id}`}
                    value={skill.level}
                    onChange={(e) => handleChange(skill.id, 'level', e.target.value)}
                  >
                    {SKILL_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {t(`cv.skillLevels.${level}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="editor-field">
                  <label htmlFor={`skill-category-${skill.id}`}>{t('fields.category')}</label>
                  <input
                    id={`skill-category-${skill.id}`}
                    type="text"
                    value={skill.category}
                    onChange={(e) => handleChange(skill.id, 'category', e.target.value)}
                    placeholder={t('placeholders.skillCategory')}
                    list={`skill-categories-${skill.id}`}
                  />
                  <datalist id={`skill-categories-${skill.id}`}>
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>

                <div className="editor-field editor-field-action">
                  <button
                    type="button"
                    className="editor-btn editor-btn-danger editor-btn-icon"
                    onClick={() => handleRemove(skill.id)}
                    aria-label={t('actions.remove', { item: skillLabel })}
                  >
                    ×
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <button type="button" className="editor-add-btn" onClick={handleAdd}>
        <span aria-hidden="true">+</span> {t('actions.addSkill')}
      </button>
    </>
  )
}
